import { Alert, Link, Stack, Typography } from '@mui/material'

import type { ThrottleFeedback } from '../hooks/useLoginThrottle'
import { useThrottleTexts } from '../hooks/useThrottleTexts'

interface Props {
  /** the `none` phase never reaches here — the parent decides whether to render */
  throttle: Exclude<ThrottleFeedback, { phase: 'none' }>
  /** starts the password-recovery flow from the lockout banner */
  onResetPassword: () => void
}

/**
 * Renders the failed-login feedback above the login fields. Lives in the
 * `LoginFormContainer` `form` slot rather than its `alert` prop, because the
 * warning phase stacks two banners and `alert` only holds one.
 */
export function ThrottleAlerts(props: Props) {
  const { throttle, onResetPassword } = props
  const texts = useThrottleTexts()

  switch (throttle.phase) {
    case 'error':
      return (
        <Alert severity="error" data-testid="login-alert-error">
          {texts.invalidCredentials}
        </Alert>
      )

    case 'warning':
      return (
        <Stack spacing={1}>
          <Alert severity="error" data-testid="login-alert-error">
            {texts.invalidCredentials}
          </Alert>
          <Alert severity="warning" data-testid="login-alert-warning">
            {texts.attemptsRemaining(throttle.attemptsRemaining)}
          </Alert>
        </Stack>
      )

    case 'locked-live':
      return (
        <LockedAlert
          variant="live"
          message={texts.lockedLive(formatCountdown(throttle.remainingMs))}
          onResetPassword={onResetPassword}
        />
      )

    case 'locked-static':
      // `unlockAtDisplay` arrives already formatted in the account's own time
      // format — render it as it came, never re-format it
      return (
        <LockedAlert
          variant="static"
          message={texts.lockedStatic(throttle.unlockAtDisplay)}
          onResetPassword={onResetPassword}
        />
      )
  }
}

interface LockedAlertProps {
  message: string
  /** `live` re-renders every second, so its message cannot sit in a live region */
  variant: 'live' | 'static'
  onResetPassword: () => void
}

/** The lockout banner, with the inline link into the password-recovery flow. */
function LockedAlert(props: LockedAlertProps) {
  const { message, variant, onResetPassword } = props
  const texts = useThrottleTexts()

  // MUI's Alert defaults to `role="alert"`, an assertive live region. The live
  // variant rewrites its message once a second, which would make a screen reader
  // interrupt and re-read the whole banner on every tick — so that variant drops
  // the live region and announces a countdown-free sentence exactly once instead.
  // The visible message stays in the accessibility tree, just no longer announced.
  const live = variant === 'live'

  return (
    <Alert severity="error" role={live ? 'presentation' : 'alert'} data-testid="login-alert-locked">
      {live && (
        <Typography component="span" role="alert" sx={srOnlyStyle}>
          {texts.lockedAnnouncement}
        </Typography>
      )}
      <Typography variant="body2">{`${message} ${texts.wasntYou}`}</Typography>
      <Typography variant="body2" sx={resetLineStyle}>
        <Link
          component="button"
          type="button"
          onClick={onResetPassword}
          data-testid="login-alert-reset-password"
        >
          {texts.resetPassword}
        </Link>
      </Typography>
    </Alert>
  )
}

const resetLineStyle = { mt: 1 }

const srOnlyStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const

/** `mm:ss`, rounded up so the last second reads `00:01` rather than `00:00` */
function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${pad(minutes)}:${pad(seconds)}`
}

function pad(value: number): string {
  return value.toString().padStart(2, '0')
}
